import React from 'react';
import analyticsWrapper from 'helpers/analytics/analyticsWrapper';
import Cookie from 'utils/cookies';
import { getBreadcrumb } from '../GameBreadcrumbsHelper';

export const getGamePageValidity = (gameType, gameid) => {
  const validGameTypes = [
    'crossword',
    'mini-crossword',
    'sudoku',
    'factchecker',
    'location-guesser',
    'loop-a-loop',
    'whos-that',
  ];

  // Check if gameType is valid
  const isGameTypeValid = validGameTypes.includes(gameType);

  // Valid cases:
  // 1. No gameType and no gameid (Games listing)
  // 2. Valid gameType with any gameid (including 'practice' or no gameid)
  return (!gameType && !gameid) || isGameTypeValid;
};

export const fireGa4 = (ga4Type, ga4Category, ga4Label, event) => {
  analyticsWrapper('ga4', {
    event: event || 'user_click',
    type: ga4Type || '',
    category: ga4Category || '',
    label: ga4Label || '',
  });
};

export const fireGa4andGrx = (
  ga4Type,
  event,
  pageTemplate,
  subSection,
  isMobile,
  gameId,
  label,
  level,
  category,
) => {
  let grxId = Cookie.get('_grx');
  let ssoId = Cookie.get('ssoid');
  grxId = grxId || '';
  ssoId = ssoId || '';
  const gaData = {
    event: event || 'user_click_games',
    type: ga4Type || 'N/A',
    page_template: pageTemplate || 'N/A',
    section: 'games',
    subsection: subSection || 'N/A',
    browsing_platform: isMobile ? 'mweb' : 'web',
    game_id: gameId,
    level: level || 'N/A',
    game_of_the_day: window.location.href.toLowerCase().includes('/today'),
    login_status: !!ssoId,
    user_grx_id: grxId,
  };
  if (ssoId) {
    gaData.sso_id = ssoId;
  }
  if (label) {
    gaData.label = label;
  }
  if (category) {
    gaData.category = category;
  }
  analyticsWrapper('ga4', gaData);
  analyticsWrapper('grx', 'track', event || 'user_click_games', gaData);
};

export const getBreadcrumbData = (gameType, gameid) => {
  let breadCrumb;
  switch (gameType) {
    case 'crossword':
      if (gameid === 'practice') {
        breadCrumb = 'crosswordpractice';
      } else if (gameid && gameid !== 'practice') {
        breadCrumb =
          gameid === 'today' ? 'crosswordtoday' : 'crosswordpractice';
      } else {
        breadCrumb = 'crossword';
      }
      break;
    case 'mini-crossword':
      if (gameid === 'practice') {
        breadCrumb = 'minicrosswordpractice';
      } else if (gameid && gameid !== 'practice') {
        breadCrumb =
          gameid === 'today' ? 'minicrosswordtoday' : 'minicrosswordpractice';
      } else {
        breadCrumb = 'minicrossword';
      }
      break;
    case 'sudoku':
      if (gameid === 'practice') {
        breadCrumb = 'sudokupractice';
      } else if (gameid && gameid !== 'practice') {
        breadCrumb = gameid === 'today' ? 'sudokutoday' : 'sudokupractice';
      } else {
        breadCrumb = 'sudoku';
      }
      break;
    case 'factchecker':
      breadCrumb = 'sudoku';
      break;
    case 'location-guesser':
      if (gameid === 'practice') {
        breadCrumb = 'locationguesserpractice';
      } else if (gameid && gameid !== 'practice') {
        breadCrumb =
          gameid === 'today'
            ? 'locationguessertoday'
            : 'locationguesserpractice';
      } else {
        breadCrumb = 'locationguesser';
      }
      break;
    case 'loop-a-loop':
      if (gameid === 'practice') {
        breadCrumb = 'loopalooppractice';
      } else if (gameid && gameid !== 'practice') {
        breadCrumb =
          gameid === 'today' ? 'loopalooptoday' : 'loopalooppractice';
      } else {
        breadCrumb = 'loopaloop';
      }
      break;
    case 'whos-that':
      if (gameid === 'practice') {
        breadCrumb = 'whosthatpractice';
      } else if (gameid && gameid !== 'practice') {
        breadCrumb = gameid === 'today' ? 'whosthattoday' : 'whosthatpractice';
      } else {
        breadCrumb = 'whosthat';
      }
      break;
    default:
      break;
  }

  if (!breadCrumb) {
    breadCrumb = 'games';
  }
  const breadCrumbData = getBreadcrumb(breadCrumb);

  return breadCrumbData;
};

export const getGameComponent = async (gameType, gameid, isMobile) => {
  let componentToRender;
  let dynamicChunks = [];
  let isGameplayPage = false;
  let isGamesPracticePage = false;
  const chunkPrefix = isMobile ? 'mobile' : 'desktop';

  if (gameid === 'practice') {
    const { default: Practice } = isMobile
      ? await import(/* webpackChunkName: "toi_gamesmobile_practice" */ `../layouts/mobile/Practice`)
      : await import(/* webpackChunkName: "toi_gamesdesktop_practice" */ `../layouts/desktop/Practice`);
    componentToRender = <Practice gameType={gameType} />;
    dynamicChunks = [`toi_games${chunkPrefix}_practice`];

    isGamesPracticePage = true;
  } else if (gameid && gameid !== 'practice') {
    const isToday = gameid === 'today';

    if (gameType === 'crossword' || gameType === 'mini-crossword') {
      const { default: Crossword } = isMobile
        ? await import(/* webpackChunkName: "toi_gamesmobile_crossword" */ `../layouts/mobile/Crossword`)
        : await import(/* webpackChunkName: "toi_gamesdesktop_crossword" */ `../layouts/desktop/Crossword`);

      componentToRender = <Crossword isToday={isToday} type={gameType} />;
      dynamicChunks = [`toi_games${chunkPrefix}_crossword`];
    } else if (gameType === 'sudoku') {
      const { default: Sudoku } = isMobile
        ? await import(/* webpackChunkName: "toi_gamesmobile_sudoku" */ `../layouts/mobile/Sudoku`)
        : await import(/* webpackChunkName: "toi_gamesdesktop_sudoku" */ `../layouts/desktop/Sudoku`);
      componentToRender = <Sudoku isToday={isToday} />;
      dynamicChunks = [`toi_games${chunkPrefix}_sudoku`];
    } else if (gameType === 'factchecker') {
      const { default: FactChecker } = isMobile
        ? await import(/* webpackChunkName: "toi_gamesmobile_factchecker" */ `../layouts/mobile/FactChecker`)
        : await import(/* webpackChunkName: "toi_gamesdesktop_factchecker" */ `../layouts/desktop/FactChecker`);
      componentToRender = <FactChecker isToday={isToday} />;
      dynamicChunks = [`toi_games${chunkPrefix}_factchecker`];
    } else if (gameType === 'location-guesser') {
      const { default: LocationGuesser } = isMobile
        ? await import(/* webpackChunkName: "toi_gamesmobile_locationguesser" */ `../layouts/mobile/LocationGuesser`)
        : await import(/* webpackChunkName: "toi_gamesdesktop_locationguesser" */ `../layouts/desktop/LocationGuesser`);
      componentToRender = <LocationGuesser isToday={isToday} />;
      dynamicChunks = [`toi_games${chunkPrefix}_locationguesser`];
    } else if (gameType === 'loop-a-loop') {
      const { default: LoopALoop } = isMobile
        ? await import(/* webpackChunkName: "toi_gamesmobile_loopaloop" */ `../layouts/mobile/LoopALoop`)
        : await import(/* webpackChunkName: "toi_gamesdesktop_loopaloop" */ `../layouts/desktop/LoopALoop`);
      componentToRender = <LoopALoop isToday={isToday} />;
      dynamicChunks = [`toi_games${chunkPrefix}_loopaloop`];
    } else if (gameType === 'whos-that') {
      const { default: WhoIsThat } = isMobile
        ? await import(/* webpackChunkName: "toi_gamesmobile_whos_that" */ `../layouts/mobile/WhoIsThat`)
        : await import(/* webpackChunkName: "toi_gamesdesktop_whos_that" */ `../layouts/desktop/WhoIsThat`);
      componentToRender = <WhoIsThat isToday={isToday} />;
      dynamicChunks = [`toi_games${chunkPrefix}_whos_that`];
    }
    isGameplayPage = true;
  } else if (
    gameType &&
    (gameType === 'crossword' ||
      gameType === 'mini-crossword' ||
      gameType === 'sudoku' ||
      gameType === 'location-guesser' ||
      gameType === 'loop-a-loop' ||
      gameType === 'whos-that')
  ) {
    const { default: GamesCatListing } = isMobile
      ? await import(/* webpackChunkName: "toi_gamesmobile_gamescatlisting" */ `../layouts/mobile/GamesCatListing`)
      : await import(/* webpackChunkName: "toi_gamesdesktop_gamescatlisting" */ `../layouts/desktop/GamesCatListing`);
    componentToRender = <GamesCatListing gameType={gameType} />;
    dynamicChunks = [`toi_games${chunkPrefix}_gamescatlisting`];
  } else {
    const { default: GamesListing } = isMobile
      ? await import(/* webpackChunkName: "toi_gamesmobile_gameslisting" */ `../layouts/mobile/GamesListing`)
      : await import(/* webpackChunkName: "toi_gamesdesktop_gameslisting" */ `../layouts/desktop/GamesListing`);
    componentToRender = <GamesListing gameType={gameType} />;
    dynamicChunks = [`toi_games${chunkPrefix}_gameslisting`];
  }
  return {
    componentToRender,
    dynamicChunks,
    isGamesPracticePage,
    isGameplayPage,
  };
};

export const gamesList = {
  items: [
    {
      tn: 'gameCategory',
      gameCategoryData: {
        title: 'Loop the \nLoop',
        caption: 'Puzzle Circuit',
        backgroundColor: '#C6F5D1',
        isEnabled: true,
        gameType: 'loop_a_loop',
        gameStatus: [
          {
            hasPlayed: false,
            hasPaused: false,
            subType: 'Easy',
          },
          {
            hasPlayed: false,
            hasPaused: false,
            subType: 'Medium',
          },
          {
            hasPlayed: false,
            hasPaused: false,
            subType: 'Hard',
          },
        ],
      },
    },
    {
      tn: 'gameCategory',
      gameCategoryData: {
        title: 'Location \nGuesser',
        caption: 'Guess the Spot',
        backgroundColor: '#c4ebeb',
        isEnabled: true,
        gameType: 'location_guesser',
        gameStatus: [
          {
            hasPlayed: false,
            hasPaused: false,
          },
        ],
      },
    },
    {
      tn: 'gameCategory',
      gameCategoryData: {
        title: 'Mini \nCrossword',
        caption: 'Lite Challenge',
        backgroundColor: '#F5C6C7',
        isEnabled: true,
        gameType: 'mini_crossword',
        gameStatus: [
          {
            hasPlayed: false,
            hasPaused: false,
          },
        ],
      },
    },
    {
      tn: 'gameCategory',
      gameCategoryData: {
        title: 'Daily \nCrossword',
        caption: 'Special puzzle',
        backgroundColor: '#F5E2C6',
        isEnabled: true,
        gameType: 'crossword',
        gameStatus: [
          {
            hasPlayed: false,
            hasPaused: false,
          },
        ],
      },
    },
    {
      tn: 'gameCategory',
      gameCategoryData: {
        title: 'Classic \nSudoku',
        caption: 'Number Matrix',
        backgroundColor: '#CADEFD',
        isEnabled: true,
        gameType: 'sudoku',
        gameStatus: [
          {
            hasPlayed: false,
            hasPaused: false,
            subType: 'Easy',
          },
          {
            hasPlayed: false,
            hasPaused: false,
            subType: 'Medium',
          },
          {
            hasPlayed: false,
            hasPaused: false,
            subType: 'Hard',
          },
        ],
      },
    },
    // {
    //   tn: 'gameCategory',
    //   gameCategoryData: {
    //     title: "Who's \nThat?",
    //     caption: 'Behind the Jersey',
    //     backgroundColor: '#D8D8FB',
    //     isEnabled: true,
    //     gameType: 'whos_that',
    //     gameStatus: [
    //       {
    //         hasPlayed: false,
    //         hasPaused: false,
    //       },
    //     ],
    //   },
    // },
  ],
};

export const getAppGameComponent = async (
  gameType,
  isResultView,
  pfm,
  theme,
  fv,
  grxId,
  difficultyLevel,
) => {
  let componentToRender;
  let dynamicChunks = [];

  if (gameType && gameType === 'loopaloop') {
    const {
      default: AppLoopALoop,
    } = await import(/* webpackChunkName: "toi_app_games" */ `../components/mobile/apploopaloop/AppLoopALoop`);
    componentToRender = (
      <AppLoopALoop
        isResultView={isResultView}
        pfm={pfm}
        theme={theme}
        fv={fv}
        grxId={grxId}
        difficultyLevel={difficultyLevel}
      />
    );
    dynamicChunks = ['toi_app_games'];
  }
  return {
    componentToRender,
    dynamicChunks,
  };
};

// Common message handler with callback support
export const handleIframeMessage = (data, callbacks = {}) => {
  const {
    onLoginSuccess,
    onLoginFailure,
    onLoginStatus,
    onCustomAction,
    onGameDataReceived,
    onGameDataSaved,
  } = callbacks;

  switch (data.action) {
    case 'onuserLogin':
      onLoginSuccess?.(data);
      break;
    case 'loginFailure':
      onLoginFailure?.(data);
      break;
    case 'userLogin':
      onLoginStatus?.(data);
      break;
    case 'gamesdata':
      onGameDataReceived?.(data);
      break;
    case 'gamesdatasaved':
      onGameDataSaved?.(data);
      break;
    default:
      onCustomAction?.(data);
      break;
  }
};

// Initialize message listener with callback support
export const initializeIframeMessageListener = callbacks => {
  const handleMessage = event => {
    if (event.data && event.data.action) {
      handleIframeMessage(event.data, callbacks);
    }
  };

  window.addEventListener('message', handleMessage);
  return () => window.removeEventListener('message', handleMessage);
};

// Initiate iframe login
export const initiateIframeLogin = (source, lang) => {
  window.top.postMessage(
    {
      action: 'initiateLogin',
      source,
      lang,
    },
    '*',
  );
};

// Check user login status
export const checkIframeUserLogin = (source, lang) => {
  window.top.postMessage(
    {
      action: 'checkUserLogin',
      source,
      lang,
    },
    '*',
  );
};

// Save games data to parent window
export const saveIframeGamesData = ({ key, value }) => {
  window.top.postMessage(
    {
      action: 'savegamesdata',
      localStorageKey: key,
      localStorageValue: value,
    },
    '*',
  );
};

// Fetch games data from parent window
export const fetchIframeGamesData = key => {
  window.top.postMessage(
    {
      action: 'fetchgamesdata',
      localStorageKey: key,
    },
    '*',
  );
};

// Delete games data from parent window
export const deleteIframeGamesData = key => {
  window.top.postMessage(
    {
      action: 'deletegamesdata',
      localStorageKey: key,
    },
    '*',
  );
};

export default getGameComponent;
